import { useCallback } from "react";
import { ethers } from "ethers";
import { ChainIdEnum } from "@/types/enums/WalletTypeEnum";

interface EVMTransactionResult {
  fetchEVMBalance: (
    address: string,
    rpcUrl: string
  ) => Promise<string | number>;
  isValidEvmAddress: (address: string) => boolean;
  estimateWithdrawFee: (
    fromAddress: string,
    toAddress: string,
    rpcUrl: string,
    data?: string
  ) => Promise<string | null>;
  getERC20Balance : (rpcUrl: string, address: string , wallet : string) => Promise<string | number>
}

export const useEVMTransaction = (): EVMTransactionResult => {
  const fetchEVMBalance = useCallback(
    async (address: string, rpcUrl: string) => {
      if (!ethers.utils.isAddress(address)) {
        return "Invalid Ethereum address";
      }

      try {
        const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
        const balanceWei = await provider.getBalance(address);
        const balanceEth = ethers.utils.formatEther(balanceWei);
        console.log("balanceEth", balanceEth);

        return Number(balanceEth);
      } catch (err) {
        return 0;
      }
    },
    []
  );
  const isValidEvmAddress = (address: string): boolean => {
    return ethers.utils.isAddress(address);
  };

  // ✅ Estimate Withdraw Fee (Gas Fee)
  const estimateWithdrawFee = useCallback(
    async (
      fromAddress: string,
      toAddress: string,
      rpcUrl: string,
      data: string = "0x"
    ) => {
      if (
        !ethers.utils.isAddress(fromAddress) ||
        !ethers.utils.isAddress(toAddress)
      ) {
        console.warn("Invalid sender or recipient address");
        return null;
      }

      try {
        const provider = new ethers.providers.JsonRpcProvider(rpcUrl);

        // ✅ 1. Check if 'to' is a contract
        const isContract = (await provider.getCode(toAddress)) !== "0x";

        // ✅ 2. Ensure 'from' has enough balance
        const balanceWei = await provider.getBalance(fromAddress);
        if (balanceWei.lte(ethers.utils.parseEther("0.0001"))) {
          console.warn("Insufficient balance for gas fees");
          return null;
        }

        // ✅ 3. Adjust 'data' for contracts vs wallets
        const txData = isContract ? data : "0x";

        // ✅ 4. Try estimating gas
        let gasLimit;
        try {
          gasLimit = await provider.estimateGas({
            from: fromAddress,
            to: toAddress,
            data: txData,
          });
        } catch (error) {
          console.warn("Gas estimation failed, using manual gas limit.");
          gasLimit = ethers.BigNumber.from(21000); // Default for wallet-to-wallet transfers
        }

        // ✅ 5. Get gas price
        const gasPrice = await provider.getGasPrice();

        // ✅ 6. Calculate fee
        const fee = gasLimit.mul(gasPrice);
        return ethers.utils.formatEther(fee);
      } catch (error) {
        console.error("Error estimating withdraw fee:", error);
        return null;
      }
    },
    []
  );

  const getERC20Balance = useCallback(
    async (rpcUrl: string, address: string , wallet : string) => {
      if (!ethers.utils.isAddress(wallet)) {
        return "Invalid Ethereum address";
      }

      try {
        const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
        const abi = ["function balanceOf(address) view returns (uint256)"];
        const contract = new ethers.Contract(address, abi, provider);

        const balance = await contract.balanceOf(wallet);
        return Number(ethers.utils.formatUnits(balance, 6)); // ✅ Works in ethers v5
      } catch (err) {
        return 0;
      }
    },
    []
  );

  return { fetchEVMBalance, isValidEvmAddress, estimateWithdrawFee , getERC20Balance };
};
